import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, Input, signal, WritableSignal } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { GbDashboardSharedUtilsModule } from '@galabid/gb-dashboard/shared/utils'
import { TranslateModule } from '@ngx-translate/core'
import {
  TeamScore,
  TeamScoringWidget,
} from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.models'
import { GbPresenterColorResolverService } from 'libs/gb-dashboard/presenter/utils/src/lib/gb-presenter-color-resolver.service'
import { GbCurrencyPipe } from 'libs/shared/utils/pipes/src/lib/gb-currency-pipe/gb-currency.pipe'
import { GbColorService } from 'libs/shared/utils/services/src/lib/gb-color-service/gb-color.service'
import { BehaviorSubject, combineLatest, interval, Observable, of } from 'rxjs'
import { filter, map, shareReplay, startWith, switchMap } from 'rxjs/operators'
import { GbPresenterWidgetPlaceholderComponent } from '../gb-presenter-widget-placeholder/gb-presenter-widget-placeholder.component'
import { PRESENTER_TEAM_SCORING_TEST_SCORES } from './gb-presenter-widget-team-scoring.test-team-scores'
@Component({
  selector: 'gb-presenter-widget-team-scoring',
  imports: [
    CommonModule,
    TranslateModule,
    GbPresenterWidgetPlaceholderComponent,
    GbDashboardSharedUtilsModule,
    GbCurrencyPipe,
  ],
  templateUrl: './gb-presenter-widget-team-scoring.component.html',
  styleUrl: './gb-presenter-widget-team-scoring.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterWidgetTeamScoringComponent {
  private widgetSubject = new BehaviorSubject<TeamScoringWidget | null>(null)

  @Input() set testData(testData: boolean) {
    this.testDataSignal.set(testData)
  }

  testDataSignal: WritableSignal<boolean> = signal(false)

  @Input() set teamScoringWidget(value: TeamScoringWidget) {
    this._teamScoringWidget = value
    const current = this.widgetSubject.value
    if (!current || this.hasTeamScoringWidgetChanged(current, value)) {
      this.widgetSubject.next(value)
    }
  }

  get teamScoringWidget(): TeamScoringWidget {
    return this._teamScoringWidget
  }

  private _teamScoringWidget!: TeamScoringWidget

  teamScoringWidget$: Observable<TeamScoringWidget> = this.widgetSubject.pipe(
    filter((widget): widget is TeamScoringWidget => !!widget),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  visibleTeamScores$ = combineLatest([toObservable(this.testDataSignal), this.teamScoringWidget$]).pipe(
    map(([testData, widget]) => {
      const hiddenTeamUuids = new Set(widget.hidden_teams || [])
      if (testData) {
        return PRESENTER_TEAM_SCORING_TEST_SCORES
      }
      return (widget.team_scores || []).filter((teamScore) => !hiddenTeamUuids.has(teamScore.uuid))
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  sortedTeamScores$ = this.visibleTeamScores$.pipe(
    map((teamScores) => {
      return [...teamScores].sort((a, b) => {
        const orderA = a.order ?? 0
        const orderB = b.order ?? 0
        if (orderA !== orderB) {
          return orderA - orderB
        }
        return (a.name || '').localeCompare(b.name || '')
      })
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  pageSize$ = this.teamScoringWidget$.pipe(
    map((widget) => Math.max(1, (widget.rows || 1) * (widget.columns || 1))),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  slideIntervalSeconds$ = this.teamScoringWidget$.pipe(
    map((widget) => Math.max(1, Number(widget.slide_interval || 10))),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  totalPages$ = combineLatest([this.sortedTeamScores$, this.pageSize$]).pipe(
    map(([teamScores, pageSize]) => {
      if (!pageSize || !teamScores?.length) return 1
      return Math.max(1, Math.ceil(teamScores.length / pageSize))
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  pageIndex$ = combineLatest([this.sortedTeamScores$, this.pageSize$, this.teamScoringWidget$]).pipe(
    switchMap(([teamScores, pageSize, widget]) => {
      if (!pageSize || !teamScores?.length) return of(0)

      if (widget.transition !== 'fade-in') return of(0)

      const totalPages = Math.max(1, Math.ceil(teamScores.length / pageSize))
      const slideIntervalSeconds = Math.max(1, Number(widget.slide_interval || 10))

      return interval(slideIntervalSeconds * 1000).pipe(
        startWith(0),
        map((tick) => tick % totalPages),
      )
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  displayTeamScores$ = combineLatest([this.sortedTeamScores$, this.pageSize$, this.pageIndex$]).pipe(
    map(([teamScores, pageSize, pageIndex]) => {
      if (!pageSize) return []

      const start = pageIndex * pageSize
      return (teamScores || []).slice(start, start + pageSize)
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  pages$ = combineLatest([this.sortedTeamScores$, this.pageSize$]).pipe(
    map(([teamScores, pageSize]) => {
      if (!pageSize) return []

      const safeScores = teamScores || []
      const pages: TeamScore[][] = []
      for (let i = 0; i < safeScores.length; i += pageSize) {
        pages.push(safeScores.slice(i, i + pageSize))
      }
      return pages
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  pagesLoop$ = combineLatest([this.pages$, this.totalPages$]).pipe(
    map(([pages, totalPages]) => {
      if (totalPages <= 1) return pages
      return [...pages, ...pages]
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  carouselLoopDurationMs$ = combineLatest([this.slideIntervalSeconds$, this.totalPages$]).pipe(
    map(([intervalSeconds, totalPages]) => {
      if (totalPages <= 1) return 0
      return intervalSeconds * totalPages * 1000
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  carouselLoopTranslatePercent$ = this.totalPages$.pipe(
    map((totalPages) => {
      if (totalPages <= 1) return 0
      return totalPages * 100
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  constructor(
    private gbColorService: GbColorService,
    private colorResolver: GbPresenterColorResolverService,
  ) {}

  private hasTeamScoringWidgetChanged(a: TeamScoringWidget, b: TeamScoringWidget): boolean {
    return (
      a.uuid !== b.uuid ||
      a.rows !== b.rows ||
      a.columns !== b.columns ||
      a.slide_interval !== b.slide_interval ||
      a.transition !== b.transition ||
      JSON.stringify(a.hidden_teams || []) !== JSON.stringify(b.hidden_teams || []) ||
      JSON.stringify(a.team_scores || []) !== JSON.stringify(b.team_scores || [])
    )
  }

  get visibleTeamScores() {
    const hiddenTeamUuids = new Set(this.hiddenTeamUuids)

    return (this.teamScoringWidget.team_scores || []).filter((teamScore) => !hiddenTeamUuids.has(teamScore.uuid))
  }

  get hiddenTeamUuids(): string[] {
    return this.teamScoringWidget.hidden_teams || []
  }

  get backgroundColor(): string {
    return this.colorResolver.resolve(this.teamScoringWidget.table_background_color) || 'transparent'
  }

  get opacity(): number {
    return this.teamScoringWidget.opacity ?? 1
  }

  get fontSizeScale(): number {
    const fontSize = this.teamScoringWidget.font_size ?? 12
    return fontSize / 12
  }

  get fontFamily(): string {
    return this.teamScoringWidget.font_family || this.colorResolver.globalFontFamily() || ''
  }

  get fontWeight(): string {
    return this.teamScoringWidget.font_weight || ''
  }

  get textAlign(): string {
    return this.teamScoringWidget.text_align || 'center'
  }

  get rowCount(): number {
    return Math.max(this.teamScoringWidget.rows ?? 1, 1)
  }

  get borderWidth(): number {
    return (this.teamScoringWidget.border_width || 0) / 10
  }

  get borderColor(): string {
    return this.colorResolver.resolve(this.teamScoringWidget.border_color) || 'transparent'
  }

  get borderStyle(): string {
    const width = this.teamScoringWidget.border_width || 0
    return width > 0 ? this.teamScoringWidget.border_style || 'solid' : 'none'
  }

  get borderRadius(): string | null {
    if (this.teamScoringWidget.border_radius === null || this.teamScoringWidget.border_radius === undefined) {
      return null
    }

    return `${this.teamScoringWidget.border_radius}${this.teamScoringWidget.border_radius_unit || 'px'}`
  }

  get accentColor(): string {
    return this.colorResolver.resolve(this.teamScoringWidget.table_accent_color) || 'var(--gb-accent-color-500)'
  }

  get primaryColor(): string {
    return this.colorResolver.resolve(this.teamScoringWidget.table_primary_color) || 'var(--gb-primary-color-500)'
  }

  get accentTextColor(): string {
    return this.gbColorService.getContrast(this.accentColor)
  }

  get primaryTextColor(): string {
    const override = this.colorResolver.resolve(this.teamScoringWidget.primary_text_color)
    if (override && this.gbColorService.isContrasting(this.primaryColor, override)) {
      return override
    }
    return this.gbColorService.getContrast(this.primaryColor)
  }
}
